import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface GoogleResponse {
    token: string;
    id: string;
    email: string;
    verified_email: boolean;
    name: string;
    given_name: string;
    family_name: string;
    picture: string;
    locale: string;
}

export interface CustomLink {
    label: string;
    url: string;
}

interface AccountRaw {
    key: string;
    name: string;
    about: string;
    business: string;
    address: string;
    phone: string;
    contactEmail: string;
    instagram: string;
    facebook: string;
    x_twitter: string;
    local: boolean;
    image?: string | null;
    custom_links?: CustomLink[];
}

export default class Account {
    key: string;
    name: string;
    about: string;
    business: string;
    address: string;
    phone: string;
    contactEmail: string;
    instagram: string;
    facebook: string;
    x_twitter: string;
    local: boolean;
    image?: string | null;
    custom_links?: CustomLink[];

    constructor(data: AccountRaw) {
        this.key = data.key;
        this.name = data.name;
        this.about = data.about;
        this.business = data.business;
        this.address = data.address;
        this.phone = data.phone;
        this.contactEmail = data.contactEmail;
        this.instagram = data.instagram;
        this.facebook = data.facebook;
        this.x_twitter = data.x_twitter;
        this.local = data.local;
        this.image = data.image;
        this.custom_links = data.custom_links ?? [];
    }

    public static readonly info = async (uuid: string) => {
        const data = (await axios
            .get(environment.api_url + "/account", {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as AccountRaw | undefined;
        if (data) return new Account(data);
        return undefined;
    };

    public static readonly update = async (
        uuid: string,
        name: string,
        about: string,
        address: string,
        phone: string,
        contactEmail: string,
        instagram: string,
        facebook: string,
        x_twitter: string,
        originalLang: string,
        image?: string | null,
        custom_links?: CustomLink[]
    ) => {
        return axios.put(
            environment.api_url + "/account",
            {
                name,
                about,
                address,
                phone,
                contactEmail,
                instagram,
                facebook,
                x_twitter,
                originalLang,
                image,
                custom_links,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
    };

    public static readonly signIn = async (email: string, password: string) =>
        (
            await axios
                .put(environment.api_url + "/auth/signin", {
                    email,
                    password,
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        )?.key as string | undefined;

    public static readonly googleSignIn = async (token: string) => {
        const data = await axios
            .get(
                `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${token}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            )
            .then((res) => res.data as GoogleResponse)
            .catch((err) => console.error(err));
        if (!data) return;
        return (
            await axios
                .put(environment.api_url + "/auth/signin", {
                    email: data.email,
                    token,
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        )?.key as string | undefined;
    };

    public static readonly signOut = async (uuid: string) =>
        await axios
            .get(environment.api_url + "/auth/signout", {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly changePassword = async (
        uuid: string,
        password: string,
        new_password: string
    ) =>
        await axios.put(
            environment.api_url + "/auth/password",
            {
                password,
                new_password,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly changeEmail = async (
        uuid: string,
        newEmail: string
    ) =>
        await axios.put(
            environment.api_url + "/account",
            {
                contactEmail: newEmail,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly requestPasswordReset = async (email: string) => {
        if (!email?.trim()) {
            throw new Error("Email is required.");
        }

        return await axios
            .post(environment.api_url + "/auth/request-password-reset", {
                email,
            })
            .then((res) => res.status === 200)
            .catch(() => false); // Silently fail, like backend
    };

    public static readonly resetPassword = async (
        token: string,
        newPassword: string
    ) => {
        return await axios
            .post(environment.api_url + "/auth/reset-password", {
                token,
                new_password: newPassword,
            })
            .then((res) => res.status === 200)
            .catch((err) => {
                if (axios.isAxiosError(err) && err.response?.data?.error) {
                    throw new Error(err.response.data.error);
                }
                throw new Error("Unexpected error while resetting password.");
            });
    };
}
